/**
 * Pure power-cost math (SIL-241 T6, spec §3.4 / SIL-230; wattage-ladder
 * fixed under SIL-351).
 *
 * ```
 * watts = resolveWatts(...) — see the ladder docstring below
 * annual_power_usd = watts/1000 * 8760 * kwh_usd * (pue ?? 1)
 * ```
 *
 * No DB access — callers resolve the per-asset raw inputs (telemetry,
 * `sku_oem_spec` power fields, GPU count) and pass plain values in.
 */

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Hours in a year — the annualization constant for `annualPowerCostUsd`. */
export const HOURS_PER_YEAR = 8760;

/** Duty-cycle fallback ratio applied to a nameplate power figure when no
 * measured draw exists (spec §3.4 — deliberately NOT the flat 0.85 an
 * earlier draft used; wayfinder-financials-power-model.md:50-54 argues for
 * 0.50 as the "mixed / typical enterprise duty" default). Applied to every
 * nameplate-derived rung (2-5) below — never to measured telemetry (rung 1). */
export const TDP_DUTY_CYCLE_FALLBACK_RATIO = 0.5;

/**
 * Host-overhead multiplier applied ONLY to the GPU-TDP-derived rung (5) —
 * GPU TDP alone excludes CPU, memory, NICs, fans, PSU loss for the rest of
 * the chassis (wayfinder-financials-power-model.md:66: "add a host overhead
 * factor (~1.3-1.5x sum-of-GPU-TDP for HGX/DGX nodes)"). 1.4 is the midpoint
 * of that range. Node-level rungs (2-4) are already whole-system figures and
 * must NOT be multiplied by this.
 */
export const HOST_OVERHEAD_FACTOR = 1.4;

/**
 * Which rung of the wattage-resolution ladder produced a `ResolvedWatts`
 * value — surfaced through `CalculationBasis` so a customer can tell measured
 * telemetry from a nameplate-derived estimate (SIL-351).
 *
 * Ladder, in resolution order (`resolveWatts`):
 *   1. `telemetry_measured`     — measured whole-system telemetry (best).
 *   2. `spec_typical_draw`      — `sku_oem_spec.typical_power_draw_watts`
 *                                 (node-level, no duty-cycle derating — the
 *                                 OEM's own typical/allocated figure).
 *   3. `spec_max_draw_derated`  — `sku_oem_spec.max_power_draw_watts` x
 *                                 duty-cycle ratio (node-level nameplate max).
 *   4. `spec_psu_derated`       — `sku_oem_spec.power_supply_watts` x
 *                                 duty-cycle ratio (node-level PSU capacity).
 *   5. `gpu_tdp_derived`        — gpu_count x tdp_watts x duty-cycle ratio x
 *                                 `HOST_OVERHEAD_FACTOR` (GPU-only nameplate,
 *                                 derived up to a whole-chassis estimate).
 *   6. `unavailable`            — no signal at all. NEVER collapses to a
 *                                 confident 0 — callers must disclose the gap
 *                                 (excluded-asset count), not report $0.
 *
 * Rungs 2-4 are node-level fields and are preferred over rung 5 precisely
 * because they don't require guessing an installed GPU count.
 */
export type WattsBasisRung =
  | 'telemetry_measured'
  | 'spec_typical_draw'
  | 'spec_max_draw_derated'
  | 'spec_psu_derated'
  | 'gpu_tdp_derived'
  | 'unavailable';

/** Raw per-asset inputs `resolveWatts` needs — one asset's telemetry +
 * `sku_oem_spec` power fields + GPU count, all nullable (sparse data is the
 * norm). */
export interface WattsResolutionInputs {
  /** Measured whole-system average watts (telemetry), last N days. */
  measuredAvgWatts: number | null;
  /** `sku_oem_spec.typical_power_draw_watts` — node-level. */
  typicalPowerDrawWatts: number | null;
  /** `sku_oem_spec.max_power_draw_watts` — node-level nameplate max. */
  maxPowerDrawWatts: number | null;
  /** `sku_oem_spec.power_supply_watts` — node-level PSU capacity. */
  powerSupplyWatts: number | null;
  /** `sku_oem_spec.tdp_watts` — PER-ACCELERATOR GPU thermal design power,
   * never a chassis figure on its own (see `gpuCount`). */
  tdpWatts: number | null;
  /** GPU count to multiply `tdpWatts` by on rung 5. Today the only source is
   * `sku_oem_spec.max_gpu_count` ("maximum GPUs supported"), NOT an installed
   * count — see `gpuCountIsMaxSupported`. */
  gpuCount: number | null;
  /** True when `gpuCount` came from a MAX-SUPPORTED field rather than an
   * actual installed count — the same max-vs-installed trap as
   * `max_ram_gb` in the embodied-carbon pipeline. Callers must pass `true`
   * unless they have a genuine installed-count source; `resolveWatts`
   * threads it straight into `ResolvedWatts.gpuCountIsMaxSupported` so the
   * overstatement risk on rung 5 is never silently hidden. */
  gpuCountIsMaxSupported: boolean;
}

export interface ResolvedWatts {
  /** Resolved wattage draw. Exactly 0 only when `basis === 'unavailable'` —
   * callers must NOT report this as a confident zero-cost forecast; disclose
   * the asset as excluded/unknown instead (SIL-351). */
  watts: number;
  basis: WattsBasisRung;
  /** Only meaningful when `basis === 'gpu_tdp_derived'` — true when the GPU
   * count backing that estimate was a max-supported figure, not an installed
   * count (see `WattsResolutionInputs.gpuCountIsMaxSupported`). */
  gpuCountIsMaxSupported: boolean;
}

/**
 * Resolves one asset's wattage draw per the SIL-351 ladder (see
 * `WattsBasisRung`'s docstring for the full ordering + rationale). Replaces
 * the old two-rung `measured ?? 0.5 * tdp_watts` resolver, which silently
 * floored to 0 W for any server SKU with no telemetry (`tdp_watts` is a
 * per-GPU field, typically NULL on server rows).
 *
 * @pure
 */
export function resolveWatts(inputs: WattsResolutionInputs): ResolvedWatts {
  if (inputs.measuredAvgWatts != null) {
    return { watts: inputs.measuredAvgWatts, basis: 'telemetry_measured', gpuCountIsMaxSupported: false };
  }
  if (inputs.typicalPowerDrawWatts != null) {
    return { watts: inputs.typicalPowerDrawWatts, basis: 'spec_typical_draw', gpuCountIsMaxSupported: false };
  }
  if (inputs.maxPowerDrawWatts != null) {
    return {
      watts: round2(TDP_DUTY_CYCLE_FALLBACK_RATIO * inputs.maxPowerDrawWatts),
      basis: 'spec_max_draw_derated',
      gpuCountIsMaxSupported: false,
    };
  }
  if (inputs.powerSupplyWatts != null) {
    return {
      watts: round2(TDP_DUTY_CYCLE_FALLBACK_RATIO * inputs.powerSupplyWatts),
      basis: 'spec_psu_derated',
      gpuCountIsMaxSupported: false,
    };
  }
  if (inputs.gpuCount != null && inputs.gpuCount > 0 && inputs.tdpWatts != null) {
    const watts = round2(
      inputs.gpuCount * inputs.tdpWatts * TDP_DUTY_CYCLE_FALLBACK_RATIO * HOST_OVERHEAD_FACTOR,
    );
    return { watts, basis: 'gpu_tdp_derived', gpuCountIsMaxSupported: inputs.gpuCountIsMaxSupported };
  }
  return { watts: 0, basis: 'unavailable', gpuCountIsMaxSupported: false };
}

/**
 * Annual power cost in USD for a resolved wattage draw, spec §3.4's formula
 * exactly: `watts/1000 * 8760 * kwh_usd * (pue ?? 1)`.
 *
 * @pure
 */
export function annualPowerCostUsd(watts: number, kwhUsd: number, pue: number | null): number {
  return round2((watts / 1000) * HOURS_PER_YEAR * kwhUsd * (pue ?? 1));
}

/**
 * Convenience: annual power cost prorated over an arbitrary number of
 * months (used by the scenario engine's monthly series, spec §3.5). Not a
 * distinct model — the same annual rate, linearly prorated.
 *
 * @pure
 */
export function powerCostOverMonths(
  watts: number,
  kwhUsd: number,
  pue: number | null,
  months: number,
): number {
  return round2((annualPowerCostUsd(watts, kwhUsd, pue) * months) / 12);
}
